import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useDeleteNotificationsMutation,
  useMarkAllNotificationsDoneMutation,
  useMarkNotificationsDoneMutation,
  useMarkNotificationsReadMutation,
} from "../../slices/NotificationCenterSlice";
import { NotificationCategory, NotificationItem } from "./notifications.types";
import {
  allDoneResult,
  chunkIds,
  errorText,
  notificationLink,
} from "./notifications.utils";

const UndoToast = ({
  text,
  onUndo,
  closeToast,
}: {
  text: string;
  onUndo: () => void;
  closeToast?: () => void;
}) => (
  <div className={"flex items-center justify-between gap-4 text-[13.5px]"}>
    <span className={"font-semibold text-[#1F2433]"}>{text}</span>
    <button
      type={"button"}
      onClick={() => {
        onUndo();
        closeToast?.();
      }}
      className={
        "rounded-[7px] px-2 py-1 text-[13px] font-extrabold text-[#6B4EFF] hover:bg-[#F0EDFF]"
      }>
      Undo
    </button>
  </div>
);

/** Open / read / done / delete, shared by the page, the bell drop-down and Home. */
export const useNotificationActions = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const [markRead] = useMarkNotificationsReadMutation();
  const [markDone] = useMarkNotificationsDoneMutation();
  const [allDone, { isLoading: isDoingAll }] =
    useMarkAllNotificationsDoneMutation();
  const [remove] = useDeleteNotificationsMutation();

  const fail = (error: unknown) =>
    toast.error(errorText(error, "That didn't work. Please try again."));

  /** Opens what the notification is about and marks it read. */
  const open = (item: NotificationItem) => {
    if (!item.isRead)
      markRead({ orgId, ids: [item.id], value: true })
        .unwrap()
        .catch(fail);
    const link = notificationLink(item);
    if (link) navigate(link);
  };

  const setRead = (ids: string[], value: boolean) =>
    markRead({ orgId, ids, value }).unwrap().catch(fail);

  /**
   * Done (or back to the inbox), with Undo in a toast. Done also marks them
   * read; Undo puts back the unread ones as they were.
   */
  const setDone = async (items: NotificationItem[], value = true) => {
    const ids = items.map((item) => item.id);
    const wereUnread = items.filter((item) => !item.isRead).map((i) => i.id);
    if (!ids.length) return;
    try {
      await markDone({ orgId, ids, value }).unwrap();
      const text = value
        ? ids.length === 1
          ? "Marked as done"
          : `${ids.length} marked as done`
        : ids.length === 1
        ? "Moved back to your inbox"
        : `${ids.length} moved back to your inbox`;
      toast(
        <UndoToast
          text={text}
          onUndo={async () => {
            try {
              await markDone({ orgId, ids, value: !value }).unwrap();
              if (value && wereUnread.length)
                await markRead({
                  orgId,
                  ids: wereUnread,
                  value: false,
                }).unwrap();
            } catch (error) {
              fail(error);
            }
          }}
        />,
        { autoClose: 5000, hideProgressBar: true, closeButton: false },
      );
    } catch (error) {
      fail(error);
    }
  };

  const deleteForever = async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await remove({ orgId, ids }).unwrap();
      toast.success(
        ids.length === 1
          ? "Notification deleted"
          : `${ids.length} notifications deleted`,
      );
    } catch (error) {
      fail(error);
    }
  };

  /**
   * "Mark all as done" (after the caller's confirm). The server leaves the
   * read/unread state alone, and returns the ids so Undo can move exactly
   * those back to the inbox.
   */
  const doneAll = async (category: NotificationCategory | "all" = "all") => {
    try {
      const { count, ids } = allDoneResult(
        await allDone({ orgId, category, returnIds: true }).unwrap(),
      );
      const text =
        count === 1
          ? "1 notification marked as done"
          : `${count} notifications marked as done`;
      if (!ids.length) {
        toast.success(text);
        return;
      }
      toast(
        <UndoToast
          text={text}
          onUndo={async () => {
            try {
              for (const chunk of chunkIds(ids))
                await markDone({ orgId, ids: chunk, value: false }).unwrap();
            } catch (error) {
              fail(error);
            }
          }}
        />,
        { autoClose: 8000, hideProgressBar: true, closeButton: false },
      );
    } catch (error) {
      fail(error);
    }
  };

  return {
    orgId,
    open,
    setRead,
    setDone,
    doneAll,
    isDoingAll,
    deleteForever,
  };
};
